import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Navigation, Clock } from "lucide-react";
import { format, isToday, isTomorrow } from "date-fns";
import { apiClient } from "@/App";
import { gigLocalDate } from "@/utils/dateUtils";
import { openExternalLink } from "@/lib/utils";
import { toast } from "sonner";
import { tokens } from "@/design-system/tokens";
import { Card, Hero, Kicker, Dot, StatStrip, StatTile, GhostBtn, UrgencyPill } from "@/design-system/primitives";

const dPal = tokens.color;
const dFont = tokens.font;
const dType = tokens.type;
const dRad = tokens.radius;
const dBorder = tokens.border;
const dMotion = tokens.motion;

function parseGigDate(dateStr) {
  const localDateStr = gigLocalDate(dateStr);
  if (!localDateStr) return null;
  const parts = localDateStr.split("-");
  if (parts.length === 3) {
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }
  return null;
}

// Admin preview (Settings → Gig Invite Page → "View Gig Invite Preview")
// links here with token="preview" — renders this real component with sample
// data instead of a hand-rolled mockup, so it can't drift out of sync with
// what invitees actually see.
const PREVIEW_GIG = {
  artist_name: "The Brass Collective",
  venue_name: "Blue Note Jazz Club",
  venue_address: "123 Blue Lane",
  city: "London",
  date: "2026-07-15",
  start_time: "20:00",
  call_time: "19:30",
  fee: 250,
  dress_code: "Smart casual",
  gig_notes: "This is a special summer series gig. Bring your own music stand. Sound check at 19:45.",
};

export default function GigPreviewPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const isPreviewMode = token === "preview";
  const [gig, setGig] = useState(isPreviewMode ? PREVIEW_GIG : null);
  const [loading, setLoading] = useState(!isPreviewMode);
  const [error, setError] = useState(false);
  const [rsvpStatus, setRsvpStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isPreviewMode) return;
    const fetchDetails = async () => {
      try {
        const res = await apiClient.get(`/rsvp/${token}`);
        setGig(res.data.gig);
        // "invited" (sent, awaiting response) must still show the RSVP
        // buttons — only "confirmed"/"declined" are a final answer.
        if (["confirmed", "declined"].includes(res.data.invitation?.status)) {
          setRsvpStatus(res.data.invitation.status);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [token, isPreviewMode]);

  const handleRsvp = async (response) => {
    if (isPreviewMode) {
      setRsvpStatus(response === "yes" ? "confirmed" : "declined");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(`/rsvp/${token}?response=${response}`);
      setRsvpStatus(response === "yes" ? "confirmed" : "declined");
      toast.success(response === "yes" ? "You're confirmed! ✓" : "Thanks for letting us know");
    } catch {
      toast.error("Failed to submit response");
    } finally {
      setSubmitting(false);
    }
  };

  const openVenueNavigation = () => {
    const address = gig.venue_address
      ? `${gig.venue_name}, ${gig.venue_address}`
      : `${gig.venue_name}, ${gig.city}`;
    openExternalLink(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`);
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: dPal.appBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 32, height: 32, border: `2px solid ${dPal.cardBorder}`, borderTopColor: dPal.orange, borderRadius: "50%", animation: "flSpin 0.8s linear infinite" }} />
        <style>{`@keyframes flSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error || !gig) {
    return (
      <div style={{ minHeight: "100vh", background: dPal.appBg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <Card maxWidth={420}>
          <div style={{ padding: 40, textAlign: "center" }}>
            <Kicker size={13}>Invite not found</Kicker>
            <p style={{ marginTop: 12, color: dPal.textMuted, fontFamily: dFont.body, fontSize: 14.5, lineHeight: 1.55 }}>
              This invite link is invalid or has expired.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  const gigDate = parseGigDate(gig.date);
  const dateUpper = gigDate ? format(gigDate, "EEE d MMM yyyy").toUpperCase() : "DATE TBA";

  // Same day-diff recipe as the in-app gig brief card — string-normalized via
  // gigLocalDate/en-CA rather than raw Date diffing, so it can't drift a day
  // across a DST boundary or timezone offset.
  let daysUntil = null;
  if (gigDate) {
    const gigDayStr = gigLocalDate(gig.date);
    const todayStr = new Date().toLocaleDateString("en-CA");
    daysUntil = Math.round((new Date(gigDayStr + "T00:00:00") - new Date(todayStr + "T00:00:00")) / 86400000);
  }

  return (
    <div style={{ minHeight: "100vh", background: dPal.appBg, padding: "40px 16px" }}>
      <Card maxWidth={760}>
        {/* Top bar */}
        <div
          style={{
            padding: "20px 32px",
            display: "flex",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
            borderBottom: dBorder.hairline,
          }}
        >
          <Kicker size={13}>You're invited</Kicker>
          {gigDate && (
            <>
              <Dot />
              <UrgencyPill daysUntil={daysUntil} isToday={isToday(gigDate)} isTomorrow={isTomorrow(gigDate)} />
            </>
          )}
          <Dot />
          <Kicker size={12} color={dPal.textDim}>{dateUpper}</Kicker>
        </div>

        {/* Hero (artist name) */}
        <Hero>{gig.artist_name}</Hero>

        {/* Call time / Gig starts / Fee strip */}
        <StatStrip columns={gig.fee ? 3 : 2}>
          <StatTile
            label="Call time"
            value={gig.call_time || "—"}
            valueColor={gig.call_time ? dPal.amber : dPal.textMuted}
          />
          <StatTile
            label="Gig starts"
            value={gig.start_time || "TBD"}
            valueColor={gig.start_time ? dPal.text : dPal.textMuted}
            last={!gig.fee}
          />
          {gig.fee && (
            <StatTile
              label="Fee"
              value={`£${Number(gig.fee).toFixed(2)}`}
              valueColor={dPal.green}
              last
            />
          )}
        </StatStrip>

        {/* Venue — appBg keeps it in the same recessed "facts" band as the
            stat strip above, instead of reading as a separate flush surface. */}
        <div style={{ padding: "20px 32px", borderBottom: dBorder.hairline, background: dPal.appBg }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <Kicker size={12} color={dPal.textDim}>Venue</Kicker>
              <div style={{ marginTop: 10, ...dType.titleMd, color: dPal.text }}>
                {gig.venue_name || "—"}
              </div>
              {(gig.venue_address || gig.city) && (
                <div style={{ marginTop: 4, ...dType.bodySm, color: dPal.textDim }}>
                  {[gig.venue_address, gig.city].filter(Boolean).join(", ")}
                </div>
              )}
            </div>
            {gig.venue_name && (
              <GhostBtn icon={Navigation} label="Directions" onClick={openVenueNavigation} primary />
            )}
          </div>
        </div>

        {/* Travel-time teaser — we don't know this recipient's home address pre-
            signup, so we can't compute a real departure/leave-by time the way
            the in-app Gig Brief does. Pitch the feature instead of faking it. */}
        <div
          style={{
            padding: "14px 32px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            borderBottom: dBorder.hairline,
            background: dPal.orangeSoft,
          }}
        >
          <Clock className="h-3.5 w-3.5" style={{ color: dPal.orange, flexShrink: 0 }} />
          <span style={{ fontFamily: dFont.body, fontSize: 13, color: dPal.text }}>
            Sign up for Fretless to get live departure and travel time to this gig from home.
          </span>
        </div>

        {/* Dress code + Notes */}
        {(gig.dress_code || gig.gig_notes) && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: gig.dress_code && gig.gig_notes ? "1fr 1.4fr" : "1fr",
              borderBottom: dBorder.hairline,
              background: dPal.cardBg,
            }}
          >
            {gig.dress_code && (
              <div style={{ padding: "20px 32px", minWidth: 0 }}>
                <Kicker size={12} color={dPal.textDim}>Dress code</Kicker>
                <p style={{ margin: "12px 0 0", ...dType.body, lineHeight: 1.6, color: dPal.text }}>
                  {gig.dress_code}
                </p>
              </div>
            )}
            {gig.gig_notes && (
              <div style={{ padding: "20px 32px", minWidth: 0, borderLeft: gig.dress_code ? dBorder.hairline : "none" }}>
                <Kicker size={12} color={dPal.textDim}>Notes</Kicker>
                <p style={{ margin: "12px 0 0", ...dType.body, lineHeight: 1.6, color: dPal.text }}>
                  {gig.gig_notes}
                </p>
              </div>
            )}
          </div>
        )}

        {/* RSVP */}
        <div style={{ padding: "24px 32px", background: dPal.appBg, borderBottom: dBorder.hairline }}>
          {!rsvpStatus ? (
            <>
              <Kicker size={12} color={dPal.textDim}>Confirm your availability</Kicker>
              <div style={{ marginTop: 14, display: "flex", gap: 12, flexWrap: "wrap" }}>
                <RsvpButton onClick={() => handleRsvp("yes")} disabled={submitting} primary fullWidth>
                  ✓ I'm in
                </RsvpButton>
                <RsvpButton onClick={() => handleRsvp("no")} disabled={submitting} fullWidth>
                  ✕ Can't make it
                </RsvpButton>
              </div>
            </>
          ) : (
            <div role="status" aria-live="polite">
              <div style={{ fontFamily: dFont.body, fontWeight: 600, fontSize: 16, color: dPal.text }}>
                {rsvpStatus === "confirmed" ? "✓ You're confirmed!" : "✕ You've declined"}
              </div>
              <p style={{ margin: "8px 0 0", ...dType.bodySm, color: dPal.textMuted }}>
                {rsvpStatus === "confirmed"
                  ? "Thanks for confirming — sign up free to get live updates if anything changes."
                  : "Thanks for letting us know."}
              </p>
              {rsvpStatus === "confirmed" && (
                <RsvpButton onClick={() => navigate("/")} primary style={{ marginTop: 16, padding: "12px 20px", fontSize: 14 }}>
                  Create Free Account
                </RsvpButton>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "18px 32px", background: dPal.appBg, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <MapPin className="h-3.5 w-3.5" style={{ color: dPal.orange, flexShrink: 0 }} />
          <span style={{ fontFamily: dFont.body, fontSize: 12.5, color: dPal.textMuted }}>
            Powered by <strong style={{ color: dPal.text }}>Fretless</strong> — the musician's command center for managing gigs.
          </span>
        </div>
      </Card>
    </div>
  );
}

// RSVP / CTA button — radius matches GhostBtn (md+1) so it reads consistently
// with the Directions button on the same page. Hover feedback mirrors the
// dress-code/notes hover idiom used in GigBriefDesktop: a fill-in on the
// ghost variant, a slight dim on the brand-filled primary variant (no token
// exists for an "orange hover" shade, so opacity is the safe choice).
function RsvpButton({ children, onClick, disabled, primary, fullWidth, style }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        flex: fullWidth ? "1 1 160px" : "0 0 auto",
        padding: "14px 20px",
        background: primary ? dPal.orange : (hover && !disabled ? dPal.innerBg : "transparent"),
        color: primary ? dPal.onOrange : dPal.textDim,
        border: primary ? dBorder.brand : dBorder.card,
        borderRadius: dRad.md + 1,
        fontFamily: dFont.body,
        fontWeight: 600,
        fontSize: 15,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.7 : (primary && hover ? 0.9 : 1),
        transition: `background ${dMotion.fast}, opacity ${dMotion.fast}`,
        ...style,
      }}
    >
      {children}
    </button>
  );
}
